// Sales Coach Réel — page de création d'une session.
// V1 : mode TRANSCRIPT entièrement fonctionnel + mode ENREGISTREMENT côté
// front uniquement (analyse réelle bloquée tant que Whisper Azure n'est pas
// provisionné). Mode UPLOAD fichier audio à venir en Phase 2.
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft, FileText, Loader2, AlertCircle, ShieldCheck, Mic, Upload, Square, Download, RotateCcw, AlertTriangle } from "lucide-react";
import { salesCoachApi, authApi } from "../lib/api";
import { detectCoachKind } from "../lib/coach-kind";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import type { RealCallContextType } from "../types";
import { MAX_AUDIO_UPLOAD_BYTES } from "../lib/sales-coach/audio-upload-limit";

interface Props {
  onBack: () => void;
  onCreated: (sessionId: string) => void;
  // Embarqué dans un sous-onglet (page Terrain) : en-tête « Débriefer mon RDV »
  // avec l'icône de la home au lieu de « Nouvelle session » + pas de bouton Retour.
  embedded?: boolean;
}

type InputMode = "transcript" | "record" | "upload";

// Les labels affichés sont traduits via t() au moment du rendu (clé i18n
// référencée ici, valeur récupérée dans le composant).
const CONTEXT_OPTIONS: Array<{ value: RealCallContextType; labelKey: string }> = [
  { value: "discovery", labelKey: "salesCoach.newSession.contextOptionDiscovery" },
  { value: "cold_call", labelKey: "salesCoach.newSession.contextOptionColdCall" },
  { value: "pitch", labelKey: "salesCoach.newSession.contextOptionPitch" },
  { value: "negotiation", labelKey: "salesCoach.newSession.contextOptionNegotiation" },
  { value: "customer_success", labelKey: "salesCoach.newSession.contextOptionCustomerSuccess" },
  { value: "qualification", labelKey: "salesCoach.newSession.contextOptionQualification" },
];

// Détection support MediaRecorder côté client (ne pas faire au top-level :
// SSR / Bun bundler pourrait évaluer `navigator` avant le runtime).
function canRecord(): boolean {
  return typeof window !== "undefined"
    && !!window.MediaRecorder
    && !!navigator.mediaDevices
    && typeof navigator.mediaDevices.getUserMedia === "function";
}

export function SalesCoachNewPage({ onBack, onCreated, embedded }: Props) {
  const { t } = useTranslation();
  // Mode par défaut = "record" (cas d'usage principal sur mobile).
  // Si l'appareil ne sait pas enregistrer (vieux navigateur, pas de micro,
  // permission bloquée au niveau OS), bascule sur "transcript" au mount.
  const [mode, setMode] = useState<InputMode>("record");
  const [transcript, setTranscript] = useState("");
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioDurationMs, setAudioDurationMs] = useState(0);
  const [contextType, setContextType] = useState<RealCallContextType | "">("");
  const [prospectName, setProspectName] = useState("");
  const [prospectCompany, setProspectCompany] = useState("");
  const [notes, setNotes] = useState("");
  const [commercialFirst, setCommercialFirst] = useState(true);
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Coachs inclus dans toutes les formules (14/10/2026), fermés sans licence ni pack actifs : le
  // serveur refuse alors le débrief (403 `coach_access_denied`). On le dit AVANT l'enregistrement,
  // comme l'accueil (`DashboardPage`), plutôt qu'au moment de l'envoi d'un rendez-vous entier.
  const [debriefFerme, setDebriefFerme] = useState(false);
  useEffect(() => {
    let actif = true;
    authApi.getAgents()
      .then((agents: { name: string; simulatorType?: string | null; isUnlocked?: boolean }[]) => {
        const debrief = agents.find((a) => detectCoachKind(a.name, a.simulatorType) === "debrief");
        if (actif) setDebriefFerme(debrief?.isUnlocked === false);
      })
      .catch(() => { /* sans liste : le serveur tranche à l'envoi, avec un message traduit */ });
    return () => { actif = false; };
  }, []);
  // Refonte mobile 2026-05-15 : déclenche le démarrage automatique du recorder
  // quand l'utilisateur tape la card hero "Enregistrer maintenant" (1 tap suffit).
  const [autoStartRecording, setAutoStartRecording] = useState(false);
  // Refonte mobile 2026-05-15 : bloc onboarding "Le combo Kestio" affiché
  // uniquement à la 1ère visite (persiste via localStorage par device).
  const [showOnboarding, setShowOnboarding] = useState(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem("salesCoachOnboarded") !== "true";
  });

  function dismissOnboarding() {
    try { localStorage.setItem("salesCoachOnboarded", "true"); } catch { /* quota / safari private mode */ }
    setShowOnboarding(false);
  }

  // Fichier audio importé : borné à 100 Mo (même limite que le serveur, `audio-upload-limit.ts`) et à 30 s minimum.
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const MAX_UPLOAD_BYTES = MAX_AUDIO_UPLOAD_BYTES;
  /** Oublie le fichier importé (repasser au micro ne doit jamais l'envoyer à la place d'un enregistrement). */
  function resetUpload() {
    setUploadFile(null);
    setUploadError(null);
    setAudioBlob(null);
    setAudioDurationMs(0);
  }
  async function pickAudioFile(file: File) {
    setUploadError(null);
    if (!file.type.startsWith("audio/") && !/\.(mp3|wav|m4a|webm|ogg|aac)$/i.test(file.name)) { setUploadError(t("salesCoach.newSession.uploadErrorType")); return; }
    if (file.size > MAX_UPLOAD_BYTES) { setUploadError(t("salesCoach.newSession.uploadErrorSize")); return; }
    const durationMs = await new Promise<number>((resolve) => {
      const el = document.createElement("audio");
      const url = URL.createObjectURL(file);
      el.preload = "metadata";
      el.onloadedmetadata = () => { const d = Number.isFinite(el.duration) ? el.duration * 1000 : 0; URL.revokeObjectURL(url); resolve(d); };
      el.onerror = () => { URL.revokeObjectURL(url); resolve(0); };
      el.src = url;
    });
    if (durationMs > 0 && durationMs < 30_000) { setUploadError(t("salesCoach.newSession.errorRecordingTooShort")); return; }
    setUploadFile(file);
    setAudioBlob(file);
    setAudioDurationMs(durationMs || 30_000);
  }

  const wordCount = transcript.trim() ? transcript.trim().split(/\s+/).length : 0;
  const tooShort = wordCount < 200;
  const audioReady = audioBlob !== null && audioDurationMs >= 30_000; // min 30s
  // Mode import : prêt seulement avec un fichier choisi ICI (pas un enregistrement micro resté en mémoire).
  const uploadReady = uploadFile !== null && audioReady;
  // Phase 2 activée : OpenAI Whisper provisionné + R2 storage
  const audioAnalysisAvailable = true;

  // Si l'appareil ne supporte pas l'enregistrement, on bascule sur transcript
  // au mount pour ne pas laisser l'utilisateur coincé sur un mode inutilisable.
  useEffect(() => {
    if (!canRecord()) {
      setMode("transcript");
    }
  }, []);

  async function handleSubmit() {
    setError(null);
    if (!consent) {
      setError(t("salesCoach.newSession.errorConsent"));
      return;
    }
    if (mode === "transcript" && tooShort) {
      setError(t("salesCoach.newSession.errorTranscriptTooShort"));
      return;
    }
    if ((mode === "record" && !audioReady) || (mode === "upload" && !uploadReady)) {
      setError(mode === "upload" ? t("salesCoach.newSession.uploadErrorMissing") : t("salesCoach.newSession.errorRecordingTooShort"));
      return;
    }
    setSubmitting(true);
    try {
      let sessionId: string;
      if (mode === "transcript") {
        const res = await salesCoachApi.createFromTranscript({
          transcriptText: transcript,
          contextType: contextType || undefined,
          contextProspectName: prospectName || undefined,
          contextProspectCompany: prospectCompany || undefined,
          contextNotes: notes || undefined,
          speakerMapping: { commercialSpeaksFirst: commercialFirst },
          consentConfirmed: consent,
        });
        sessionId = res.sessionId;
      } else if ((mode === "record" || mode === "upload") && audioBlob) {
        const ext = (audioBlob.type.split("/")[1] ?? "webm").split(";")[0];
        const res = await salesCoachApi.createFromAudio({
          audioBlob,
          audioFilename: uploadFile?.name ?? `recording-${Date.now()}.${ext}`,
          contextType: contextType || undefined,
          contextProspectName: prospectName || undefined,
          contextProspectCompany: prospectCompany || undefined,
          contextNotes: notes || undefined,
          speakerMapping: { commercialSpeaksFirst: commercialFirst },
          consentConfirmed: consent,
          language: "fr",
        });
        sessionId = res.sessionId;
      } else {
        throw new Error(t("salesCoach.newSession.errorUnsupportedMode"));
      }
      onCreated(sessionId);
    } catch (err) {
      const message = err instanceof Error ? err.message : t("salesCoach.newSession.errorUnknown");
      setError(message);
      setSubmitting(false);
    }
  }

  if (debriefFerme) {
    return (
      <div className="max-w-3xl mx-auto space-y-5 md:space-y-6">
        {!embedded && (
          <button onClick={onBack} className="flex items-center gap-2 text-sm hover:underline" style={{ color: "#384872" }}>
            <ArrowLeft className="h-4 w-4" /> {t("common.back")}
          </button>
        )}
        <Card className="p-6 flex items-start gap-3" data-testid="debrief-ferme">
          <AlertCircle className="h-5 w-5 shrink-0" style={{ color: "#C0423F" }} />
          <p className="text-sm" style={{ color: "#C0423F" }}>{t("apiErrors.coach_access_denied")}</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-5 md:space-y-6">
      {/* Header — compact sur mobile (usage terrain) */}
      <div>
        {embedded ? (
          <div className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-full text-white" style={{ background: "#5A4BBE" }}><Mic className="h-5 w-5" /></span>
            <h1 className="text-xl md:text-2xl font-bold" style={{ color: "#384872", fontFamily: "Sora, sans-serif" }}>{t("dashboard.debriefCardTitle")}</h1>
          </div>
        ) : (
          <>
            <button onClick={onBack} className="flex items-center gap-2 text-sm mb-3 md:mb-4 hover:underline" style={{ color: "#384872" }}>
              <ArrowLeft className="h-4 w-4" /> {t("common.back")}
            </button>
            <h1 className="text-xl md:text-2xl font-bold" style={{ color: "#384872", fontFamily: "Sora, sans-serif" }}>
              {t("salesCoach.newSession.title")}
            </h1>
            {/* Sous-titre allégé : caché sur mobile, court sur desktop */}
            <p className="mt-2 text-sm hidden md:block" style={{ color: "#4B5573" }}>
              {t("salesCoach.newSession.subtitle")}
            </p>
          </>
        )}
      </div>

      {/* Onboarding "Le combo Kestio" — 1ère visite uniquement.
          Persiste via localStorage (par device). L'user peut le fermer via ✕. */}
      {showOnboarding && (
        <div className="rounded-2xl p-4 flex items-start gap-3 relative" style={{ background: "linear-gradient(135deg, #F3F0FF 0%, #E8E4FF 100%)" }}>
          <div className="h-10 w-10 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: "#988EF2" }}>
            <Mic className="h-5 w-5 text-white" />
          </div>
          <div className="flex-1 pr-6">
            <p className="font-semibold text-sm" style={{ color: "#5A4BBE" }}>
              {t("salesCoach.newSession.onboardingTitle")}
            </p>
            <p className="text-xs mt-1" style={{ color: "#4B5573" }}>
              {t("salesCoach.newSession.onboardingDescription")}
            </p>
          </div>
          <button
            onClick={dismissOnboarding}
            className="absolute top-2 right-2 h-7 w-7 rounded-full flex items-center justify-center hover:bg-white/40 transition-colors"
            aria-label={t("salesCoach.newSession.onboardingDismissAria")}
            title={t("salesCoach.newSession.onboardingDismissTitle")}
          >
            <span className="text-lg leading-none" style={{ color: "#5A4BBE" }}>×</span>
          </button>
        </div>
      )}

      {/* Refonte mobile 2026-05-15 (retour Fabien) :
          UN SEUL bloc actif en haut (= l'action du mode courant), pas de sélecteur séparé.
          Le recorder/textarea/placeholder EST la zone hero. Pattern mobile-natif
          (Voice Memos iOS, WhatsApp record) : action principale dominante, alternatives dessous. */}

      {/* === BLOC ACTIF (selon le mode courant) === */}

      {/* Mode TRANSCRIPT */}
      {mode === "transcript" && (
        <Card className="p-6 space-y-4">
          <div>
            <Label htmlFor="transcript" className="text-base font-semibold" style={{ color: "#384872" }}>
              <FileText className="inline h-4 w-4 mr-2" />
              {t("salesCoach.newSession.transcriptLabel")}
            </Label>
            <Textarea
              id="transcript"
              value={transcript}
              onChange={(e) => setTranscript(e.target.value)}
              placeholder={t("salesCoach.newSession.transcriptPlaceholder")}
              className="min-h-[150px] sm:min-h-[300px] mt-2 font-mono text-sm"
            />
            <div className="flex justify-between mt-2 text-xs">
              <span style={{ color: tooShort ? "#C0423F" : "#4B5573" }}>
                {t("salesCoach.newSession.wordsCount", { count: wordCount })} {tooShort && t("salesCoach.newSession.wordsMinimum")}
              </span>
              <span style={{ color: "#6B7280" }}>{t("salesCoach.newSession.transcriptHint")}</span>
            </div>
          </div>
        </Card>
      )}

      {/* Mode ENREGISTREMENT */}
      {mode === "record" && (
        <>
          {!audioAnalysisAvailable && (
            <div className="rounded-xl p-4 border" style={{ borderColor: "#988EF2", backgroundColor: "#F8F7FF" }}>
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: "#5A4BBE" }} />
                <div className="text-sm" style={{ color: "#384872" }}>
                  <strong>{t("salesCoach.newSession.audioComingSoonTitle")}</strong> {t("salesCoach.newSession.audioComingSoonDescription")}
                </div>
              </div>
            </div>
          )}
          <AudioRecorder
            onRecordingComplete={(blob, durationMs) => { setAudioBlob(blob); setAudioDurationMs(durationMs); }}
            onReset={() => { setAudioBlob(null); setAudioDurationMs(0); }}
            blob={audioBlob}
            durationMs={audioDurationMs}
            autoStart={autoStartRecording}
            onAutoStartConsumed={() => setAutoStartRecording(false)}
          />
        </>
      )}

      {/* Mode UPLOAD : un fichier audio (export Zoom, Meet, dictaphone…) prend le même
          chemin que l'enregistrement micro (`POST /sessions/audio`, transcription Whisper). */}
      {mode === "upload" && (
        <Card className="p-6">
          <label htmlFor="sc-upload-file" className="flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed px-6 py-8 text-center hover:bg-slate-50" style={{ borderColor: "#D3CFE1" }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) void pickAudioFile(f); }}>
            <Upload className="h-10 w-10 mb-3" style={{ color: "#7CBF94" }} />
            <h3 className="text-lg font-semibold mb-1" style={{ color: "#384872" }}>{t("salesCoach.newSession.uploadTitle")}</h3>
            <p className="text-sm text-gray-500">{t("salesCoach.newSession.uploadDescription")}</p>
            <input id="sc-upload-file" type="file" accept="audio/*,.mp3,.wav,.m4a,.webm,.ogg,.aac" className="sr-only" onChange={(e) => { const f = e.target.files?.[0]; if (f) void pickAudioFile(f); }} />
          </label>
          {uploadFile && (
            <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-lg px-3 py-2 text-sm" style={{ background: "#F1F5F9", color: "#384872" }}>
              <span className="truncate">{uploadFile.name} · {t("salesCoach.newSession.fileSizeMb", { n: (uploadFile.size / (1024 * 1024)).toFixed(1) })}{audioDurationMs > 0 ? ` · ${t("salesCoach.newSession.durationMin", { n: Math.round(audioDurationMs / 60000) })}` : ""}</span>
              <button type="button" className="text-xs font-semibold" style={{ color: "#C0423F" }} onClick={resetUpload}>{t("common.delete")}</button>
            </div>
          )}
          {uploadError && <p className="mt-2 text-sm" style={{ color: "#C0423F" }}>{uploadError}</p>}
        </Card>
      )}

      {/* === ALTERNATIVES (modes différents du mode courant) === */}
      <div className="space-y-2">
        <div className="flex items-center gap-3 py-1">
          <div className="flex-1 h-px bg-gray-200" />
          <span className="text-[11px] uppercase tracking-wider font-medium" style={{ color: "#9CA3AF" }}>
            {t("salesCoach.newSession.alternativesDivider")}
          </span>
          <div className="flex-1 h-px bg-gray-200" />
        </div>
        {/* Alternative "Enregistrer" (visible si mode != record) */}
        {mode !== "record" && (
          <ModeCard
            active={false}
            // Tap depuis une alternative = bascule + autoStart (user gesture
            // chaîné pour iOS Safari → permission micro demandée immédiatement)
            onClick={() => { if (uploadFile) resetUpload(); setMode("record"); setAutoStartRecording(true); }}
            icon={<Mic className="h-5 w-5 text-white" />}
            color="#988EF2"
            title={t("salesCoach.newSession.altRecordTitle")}
            description={t("salesCoach.newSession.altRecordDescription")}
            available
          />
        )}
        {/* Alternative "Transcript" (visible si mode != transcript) */}
        {mode !== "transcript" && (
          <ModeCard
            active={false}
            onClick={() => { setMode("transcript"); setAutoStartRecording(false); }}
            icon={<FileText className="h-5 w-5 text-white" />}
            color="#384872"
            title={t("salesCoach.newSession.altTranscriptTitle")}
            description={t("salesCoach.newSession.altTranscriptDescription")}
            available
          />
        )}
        {/* Alternative "Upload" (visible si mode != upload) */}
        {mode !== "upload" && (
          <ModeCard
            active={false}
            onClick={() => setMode("upload")}
            icon={<Upload className="h-5 w-5 text-white" />}
            color="#7CBF94"
            title={t("salesCoach.newSession.altUploadTitle")}
            description={t("salesCoach.newSession.altUploadDescription")}
            available
          />
        )}
      </div>

      {/* Contexte session */}
      <Card className="p-6 space-y-4">
        <div className="font-semibold" style={{ color: "#384872" }}>{t("salesCoach.newSession.contextSectionTitle")}</div>

        <div>
          <Label htmlFor="contextType">{t("salesCoach.newSession.contextTypeLabel")}</Label>
          <select
            id="contextType"
            value={contextType}
            onChange={(e) => setContextType(e.target.value as RealCallContextType | "")}
            className="w-full mt-1 rounded-md border px-3 py-2 text-sm"
            style={{ borderColor: "#D1D5DB" }}
          >
            <option value="">{t("salesCoach.newSession.contextTypeAuto")}</option>
            {CONTEXT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{t(o.labelKey)}</option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="prospectName">{t("salesCoach.newSession.prospectNameLabel")}</Label>
            <Input id="prospectName" value={prospectName} onChange={(e) => setProspectName(e.target.value)} placeholder={t("salesCoach.newSession.prospectNamePlaceholder")} />
          </div>
          <div>
            <Label htmlFor="prospectCompany">{t("salesCoach.newSession.prospectCompanyLabel")}</Label>
            <Input id="prospectCompany" value={prospectCompany} onChange={(e) => setProspectCompany(e.target.value)} placeholder={t("salesCoach.newSession.prospectCompanyPlaceholder")} />
          </div>
        </div>

        <div>
          <Label htmlFor="notes">{t("salesCoach.newSession.notesLabel")}</Label>
          <Textarea
            id="notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t("salesCoach.newSession.notesPlaceholder")}
            className="min-h-[80px]"
          />
        </div>

        <div>
          <Label className="block mb-2">{t("salesCoach.newSession.speakerFirstLabel")}</Label>
          <div className="flex gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" checked={commercialFirst} onChange={() => setCommercialFirst(true)} />
              <span style={{ color: "#384872" }}>{t("salesCoach.newSession.speakerFirstMe")}</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" checked={!commercialFirst} onChange={() => setCommercialFirst(false)} />
              <span style={{ color: "#384872" }}>{t("salesCoach.newSession.speakerFirstProspect")}</span>
            </label>
          </div>
        </div>
      </Card>

      {/* RGPD */}
      <Card className="p-6 border-2" style={{ borderColor: "#988EF2", background: "#F8F7FF" }}>
        <div className="flex items-start gap-3">
          <ShieldCheck className="h-6 w-6 mt-0.5" style={{ color: "#5A4BBE" }} />
          <div className="flex-1">
            <div className="font-semibold mb-2" style={{ color: "#5A4BBE" }}>{t("salesCoach.newSession.rgpdTitle")}</div>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-1"
              />
              <span className="text-sm" style={{ color: "#384872" }}>
                {t("salesCoach.newSession.rgpdConsent")}
              </span>
            </label>
          </div>
        </div>
      </Card>

      {/* Erreur */}
      {error && (
        <Card className="p-4 border-2" style={{ borderColor: "#F49390", background: "#FFF5F5" }}>
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 mt-0.5" style={{ color: "#C0423F" }} />
            <div className="text-sm" style={{ color: "#C0423F" }}>{error}</div>
          </div>
        </Card>
      )}

      {/* Espace en bas pour ne pas masquer le contenu par le sticky CTA mobile.
          h-32 sur mobile car la barre fait maintenant 2 lignes (Analyser + Annuler). */}
      <div className="h-32 md:h-0" aria-hidden />

      {/* Actions — Refonte 2026-05-15 pour cohabitation avec widget Zoho Desk
          en bas-droite (~70x70px). Sur mobile, empilement vertical avec :
          - Bouton primaire "Analyser" pleine largeur en haut (zone pouce)
            mais avec pr-16 pour ne pas passer SOUS le widget Zoho
          - Bouton "Annuler" en lien texte secondaire en dessous (centré)
          Sur desktop, retour à l'alignement horizontal classique. */}
      <div
        className="fixed md:static bottom-0 left-0 right-0 z-30 bg-white md:bg-transparent border-t md:border-t-0 px-4 py-3 md:p-0 flex flex-col md:flex-row md:items-center md:justify-end gap-2 md:gap-3 shadow-lg md:shadow-none"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)" }}
      >
        <Button
          onClick={handleSubmit}
          disabled={
            submitting ||
            !consent ||
            (mode === "transcript" && tooShort) ||
            (mode === "record" && (!audioReady || !audioAnalysisAvailable)) ||
            (mode === "upload" && (!uploadReady || !audioAnalysisAvailable))
          }
          className="w-full md:w-auto md:order-2 h-12 md:h-11 text-base md:text-sm font-semibold pr-16 md:pr-4"
          style={{ background: "#384872", color: "white" }}
          title={
            mode === "record" && !audioAnalysisAvailable
              ? t("salesCoach.newSession.submitTooltipComingSoon")
              : undefined
          }
        >
          {submitting ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              {t("salesCoach.newSession.submitCreating")}
            </>
          ) : mode === "record" && !audioAnalysisAvailable ? (
            t("salesCoach.newSession.submitComingSoon")
          ) : (
            t("salesCoach.newSession.submitAnalyze")
          )}
        </Button>
        <Button
          variant="outline"
          onClick={onBack}
          disabled={submitting}
          className="w-full md:w-auto md:order-1 h-10 md:h-10 text-sm font-medium"
        >
          {t("common.cancel")}
        </Button>
      </div>
    </div>
  );
}

// ============================================================
// Sous-composants : sélecteur de mode + recorder audio
// ============================================================

function ModeCard({
  active, onClick, icon, color, title, description, available,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  color: string;
  title: string;
  description: string;
  available: boolean;
}) {
  return (
    <button
      onClick={available ? onClick : undefined}
      disabled={!available}
      className={`relative rounded-xl border bg-white p-4 text-left transition-all flex items-start gap-3 ${
        available ? "cursor-pointer hover:shadow-md hover:-translate-y-0.5" : "opacity-50 cursor-not-allowed"
      } ${active ? "ring-2 ring-offset-1" : ""}`}
      style={{ ...(active ? { boxShadow: `0 0 0 2px ${color}` } : {}) }}
    >
      <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: color }}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm" style={{ color: "#384872" }}>{title}</div>
        <div className="text-xs text-gray-500 mt-0.5">{description}</div>
      </div>
    </button>
  );
}

// Composant d'enregistrement audio via MediaRecorder de l'API Web.
// Gère permission micro, timer, lecture, téléchargement, reset.
// Compatible iOS Safari 14.3+, Chrome/Firefox/Edge modernes.
// Format : ce que le navigateur fournit par défaut (webm/opus ou mp4/aac selon plateforme).
function AudioRecorder({
  onRecordingComplete,
  onReset,
  blob,
  durationMs,
  autoStart = false,
  onAutoStartConsumed,
}: {
  onRecordingComplete: (blob: Blob, durationMs: number) => void;
  onReset: () => void;
  blob: Blob | null;
  durationMs: number;
  // Démarre l'enregistrement automatiquement au mount/changement (refonte mobile 2026-05-15).
  // Permet le pattern "1 tap → enregistrement direct" depuis la card hero.
  autoStart?: boolean;
  onAutoStartConsumed?: () => void;
}) {
  const { t } = useTranslation();
  const [recording, setRecording] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startMsRef = useRef<number>(0);

  // Timer en live pendant l'enregistrement
  useEffect(() => {
    if (!recording) return;
    const id = setInterval(() => setElapsedMs(Date.now() - startMsRef.current), 200);
    return () => clearInterval(id);
  }, [recording]);

  // Auto-start au mount si la prop autoStart est true (pattern "1 tap depuis hero").
  // Note iOS Safari : getUserMedia() est appelée dans le useEffect immédiatement après
  // le tap user, ce qui préserve le user gesture nécessaire pour la permission micro.
  useEffect(() => {
    if (autoStart && !recording && !blob && !error) {
      startRecording();
      onAutoStartConsumed?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart]);

  // Cleanup : si on quitte la page pendant un enregistrement, on libère le micro
  useEffect(() => {
    return () => {
      if (recorderRef.current && recorderRef.current.state !== "inactive") {
        recorderRef.current.stop();
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  async function startRecording() {
    setError(null);
    if (!navigator.mediaDevices || !window.MediaRecorder) {
      setError(t("salesCoach.newSession.recorderBrowserUnsupported"));
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      const r = new MediaRecorder(stream);
      r.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      r.onstop = () => {
        const finalBlob = new Blob(chunksRef.current, { type: r.mimeType || "audio/webm" });
        const finalDuration = Date.now() - startMsRef.current;
        onRecordingComplete(finalBlob, finalDuration);
        stream.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      };
      r.start();
      recorderRef.current = r;
      startMsRef.current = Date.now();
      setElapsedMs(0);
      setRecording(true);
    } catch (err) {
      const e = err as { name?: string; message?: string };
      if (e.name === "NotAllowedError" || e.name === "PermissionDeniedError") {
        setError(t("salesCoach.newSession.recorderPermissionDenied"));
      } else if (e.name === "NotFoundError") {
        setError(t("salesCoach.newSession.recorderNoMic"));
      } else {
        setError(t("salesCoach.newSession.recorderStartFailed", { message: e.message ?? t("salesCoach.newSession.errorUnknown") }));
      }
    }
  }

  function stopRecording() {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
      setRecording(false);
    }
  }

  function downloadBlob() {
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const ext = (blob.type.split("/")[1] ?? "webm").split(";")[0];
    a.download = `sales-coach-recording-${Date.now()}.${ext}`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function formatTime(ms: number): string {
    const totalSec = Math.floor(ms / 1000);
    const min = Math.floor(totalSec / 60);
    const sec = totalSec % 60;
    return `${min.toString().padStart(2, "0")}:${sec.toString().padStart(2, "0")}`;
  }

  // 3 états : initial / recording / recorded
  return (
    <Card className="p-6">
      {error && (
        <div className="mb-4 p-3 rounded-lg flex items-start gap-2 text-sm" style={{ background: "#FFF5F5", color: "#C0423F" }}>
          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <div>{error}</div>
        </div>
      )}

      {!recording && !blob && (
        <div className="text-center py-8">
          {/* Gros icône micro cliquable — déclenche aussi l'enregistrement
              (raccourci visuel cohérent avec la métaphore "appuie sur le micro"). */}
          <button
            type="button"
            onClick={startRecording}
            aria-label={t("salesCoach.newSession.recorderStartAria")}
            className="w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-4 transition-all hover:scale-105 active:scale-95 shadow-md hover:shadow-xl cursor-pointer"
            style={{ backgroundColor: "#5A4BBE" }}
          >
            <Mic className="h-12 w-12 text-white" />
          </button>
          <h3 className="font-semibold mb-1" style={{ color: "#384872", fontFamily: "Sora, sans-serif" }}>
            {t("salesCoach.newSession.recorderReadyTitle")}
          </h3>
          <p className="text-sm text-gray-500 mb-5">
            {t("salesCoach.newSession.recorderReadyDescription")}<br />
            {t("salesCoach.newSession.recorderReadyPermissionHint")}
          </p>
          {/* Bouton XL pleine largeur sur mobile, taille standard sur desktop */}
          <Button
            onClick={startRecording}
            className="w-full md:w-auto h-14 md:h-11 text-base md:text-sm shadow-lg hover:shadow-xl transition-shadow"
            style={{ background: "#5A4BBE", color: "white" }}
          >
            <Mic className="h-5 w-5 md:h-4 md:w-4 mr-2" />
            {t("salesCoach.newSession.recorderStartButton")}
          </Button>
        </div>
      )}

      {recording && (
        <div className="text-center py-8">
          <div className="relative w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: "#F49390" }}>
            {/* Cercle pulsant pour signaler l'enregistrement actif */}
            <div className="absolute inset-0 rounded-full animate-ping opacity-30" style={{ backgroundColor: "#F49390" }} />
            <div className="relative w-6 h-6 rounded-full bg-white" />
          </div>
          <div className="text-3xl font-bold mb-2" style={{ color: "#C0423F", fontFamily: "Sora, sans-serif" }}>
            {formatTime(elapsedMs)}
          </div>
          <p className="text-sm text-gray-500 mb-5">{t("salesCoach.newSession.recorderInProgress")}</p>
          <Button onClick={stopRecording} className="w-full md:w-auto h-14 md:h-11 text-base md:text-sm border-2 border-red-500 text-red-500 hover:bg-red-50 bg-white">
            <Square className="h-5 w-5 md:h-4 md:w-4 mr-2" />
            {t("salesCoach.newSession.recorderStopButton")}
          </Button>
        </div>
      )}

      {!recording && blob && (
        <div>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-11 h-11 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: "#7CBF94" }}>
              <Mic className="h-5 w-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold" style={{ color: "#384872", fontFamily: "Sora, sans-serif" }}>
                {t("salesCoach.newSession.recorderReady", { duration: formatTime(durationMs) })}
              </div>
              <div className="text-xs text-gray-500 mt-0.5">
                {t("salesCoach.newSession.fileSizeMb", { n: (blob.size / 1024 / 1024).toFixed(2) })} · {blob.type.split(";")[0]}
              </div>
            </div>
          </div>
          {/* Player audio natif */}
          <audio controls src={URL.createObjectURL(blob)} className="w-full mb-4" />
          <div className="flex gap-2 flex-wrap">
            <Button onClick={downloadBlob} variant="outline" size="sm">
              <Download className="h-4 w-4 mr-2" /> {t("salesCoach.newSession.recorderDownload")}
            </Button>
            <Button onClick={() => { onReset(); setElapsedMs(0); }} variant="outline" size="sm">
              <RotateCcw className="h-4 w-4 mr-2" /> {t("salesCoach.newSession.recorderReRecord")}
            </Button>
          </div>
          {durationMs < 30_000 && (
            <p className="text-xs mt-3" style={{ color: "#C0423F" }}>
              {t("salesCoach.newSession.recorderTooShortWarning")}
            </p>
          )}
        </div>
      )}
    </Card>
  );
}
